import React, { useEffect, useState, useRef } from 'react';
import "../styles/CodeEditor.css";
import MonacoEditor from 'react-monaco-editor';
import Loader from './Loader';
import { useSocket } from '../SocketContext';
import { jwtDecode } from 'jwt-decode';
import { SquarePen } from 'lucide-react';

export default function CodeEditor({ question, isMobile, matchDoc: matchObj, handleCodeOutput, handleSubmitResults, handleUserWonMatch, timeUp, matchLost, userWonMatch, handleGotWinnerRatingChange, openMenu, handleToggleTab }) {
  const [code, setCode] = useState(question?.default_code || "");
  const [runningCode, setRunningCode] = useState(false)
  const [submittingCode, setSubmittingCode] = useState(false)
  const socket = useSocket();
  const editorRef = useRef(null);

  // Update code state when editor content changes
  const onChange = (newValue) => {
    setCode(newValue);
    localStorage.setItem("code", JSON.stringify({ code, match_id: matchObj._id }))
  };

  useEffect(() => {
    if (!editorRef.current) return;

    const editor = editorRef.current;
    const container = editor.getDomNode().parentNode;

    const resizeObserver = new ResizeObserver(() => {
      editor.layout();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (question && matchObj?._id) {
      const savedCodeData = JSON.parse(localStorage.getItem('code'));
      if (savedCodeData && savedCodeData.match_id === matchObj._id) {
        setCode(savedCodeData.code);
      } else {
        setCode(question.default_code || "");
      }
    }
  }, [question, matchObj?._id]);

  const editorDidMount = (editor, monaco) => {
    editorRef.current = editor; // Store the editor instance
    // Define and register the Night Owl theme
    const nightOwlTheme = {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: '', foreground: 'D4D4D4', background: '011627' },
        { token: 'comment', foreground: '7a7a7a', fontStyle: 'italic' },
        { token: 'keyword', foreground: '82AAFF' },
        { token: 'number', foreground: 'FFCB6B' },
        { token: 'string', foreground: 'C3E88D' },
      ],
      colors: {
        'editor.background': '#011627',
      }
    };
    monaco.editor.defineTheme('night-owl', nightOwlTheme);

    // Disable the context menu
    editor.updateOptions({ contextmenu: false });

    // Get the editor's DOM node and disable native copy/cut/paste events
    const domNode = editor.getDomNode();
    if (domNode) {
      domNode.addEventListener('copy', (e) => e.preventDefault());
      domNode.addEventListener('cut', (e) => e.preventDefault());
      domNode.addEventListener('paste', (e) => e.preventDefault());
    }

    // Intercept keydown events to block keyboard shortcuts (Ctrl/Cmd+C, Ctrl/Cmd+X, Ctrl/Cmd+V)
    editor.onKeyDown((e) => {
      const ctrlCmd = e.ctrlKey || e.metaKey;
      if (ctrlCmd) {
        if (
          e.keyCode === monaco.KeyCode.KeyC ||
          e.keyCode === monaco.KeyCode.KeyX ||
          e.keyCode === monaco.KeyCode.KeyV
        ) {
          e.preventDefault();
        }
      }
    });

    // Focus the editor once mounted
    editor.focus();
  };

  // Editor options
  const options = {
    selectOnLineNumbers: true,
    automaticLayout: false, // Disable automatic layout
  };

  const handleRunCode = () => {
    setRunningCode(true)
    // code runnning logic goes here
    let url = process.env.REACT_APP_BACKEND_URL
    switch (matchObj.language) {
      case 'js':
        url += '/run/js'
        break
      case 'py':
        url += '/run/py'
        break
      case 'java':
        url += '/run/java'
        break
      case 'c':
        url += '/run/c'
        break

    }
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ code: code, question_id: question._id })
    })
      .then(response => response.json())
      .then(data => {
        setRunningCode(false)
        console.log("run output: ", data)
        handleCodeOutput(data)
        openMenu() // Open the menu
        handleToggleTab("output") // Display output tab

      })
      .catch(error => {
        console.error('Error running code:', error);
      });

  }

  const handleSubmitCode = () => {
    setSubmittingCode(true)
    let url = process.env.REACT_APP_BACKEND_URL
    switch (matchObj.language) {
      case 'js':
        url += '/submit/js'
        break
      case 'py':
        url += '/submit/py'
        break
      case 'java':
        url += '/submit/java'
        break
      case 'c':
        url += '/submit/c'
        break
    }
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ code, question })
    })
      .then(response => response.json())
      .then(data => {
        setSubmittingCode(false)
        console.log("submit results: ", data)
        handleSubmitResults(data)
        if (data.all_PASS) {
          handleMatchFinish()
        }
        openMenu() // Open the menu
        handleToggleTab("testcases") // Display testcases tab
      })
      .catch(error => {
        console.error('Error running code:', error);
      });
  }

  const handleMatchFinish = () => {
    //calculate solveTime
    const finishTime = new Date();
    const storedData = localStorage.getItem('matchStartData');
    let solveTime = null;

    if (storedData) {
      const { matchId, hour, min, sec } = JSON.parse(storedData);
      if (matchId === matchObj._id) {
        const startTime = new Date();
        startTime.setHours(hour, min, sec, 0);
        solveTime = Math.floor((finishTime - startTime) / 1000);
      }
    }

    const user_id = localStorage.getItem('token')
    const { _id } = jwtDecode(user_id)

    socket.emit("endMatch", {
      match: matchObj,
      winner_id: _id,
      solveTime
    }, (response) => {
      const { winner_rating, winner_rating_delta } = response
      handleGotWinnerRatingChange(winner_rating, winner_rating_delta)
    })

    handleUserWonMatch(_id)
  }
  
  const convertLangToLanguage = lang =>{
    switch(lang){
      case 'py': return "Python"
      case 'js': return "JavaScript"
    }
  }

  return (
    <div className='CodeEditor'>
      <div className='CodeEditorHeader'>
        {!isMobile ? (
          <h3>
            <span><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-code-xml-icon lucide-code-xml"><path d="m18 16 4-4-4-4" /><path d="m6 8-4 4 4 4" /><path d="m14.5 4-5 16" /></svg></span> Code
          </h3>
        ) : (
          <p style={{fontSize:"smaller"}}>
            {convertLangToLanguage(matchObj.language)}
          </p>
        )}
        <div className='code_editor_header_buttonsDiv'>
          {isMobile ? (
            <button onClick={handleRunCode} className='runBtn runBtn_mobile'>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#d5bcf5" stroke="#d5bcf52e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-play-icon lucide-play"><path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z" /></svg>
            </button>
          ) : (
            <button onClick={handleRunCode} className='runBtn'>
              {!runningCode ? "Run" : <Loader />}
            </button>
          )}
          {!timeUp && !userWonMatch && !matchLost && (
            <button onClick={handleSubmitCode} className='submitBtn'>
              {!submittingCode ? "Submit" : <Loader />}
            </button>
          )}
        </div>
      </div>
      <div className='CodeEditorContent'>
        {question != null ? <MonacoEditor
          width="100%"
          height="100%"
          scrollbarWidth="none"
          borderRadius="20px"
          language={question?.question_programming_language}
          theme="night-owl"
          value={code}
          options={options}
          onChange={onChange}
          editorDidMount={editorDidMount}
          minimap={{ enabled: false }}
        /> : <p>Loading...</p>}
      </div>

    </div >
  );
}
